import { useState, useRef, useEffect, useCallback } from "react";
import type { Inquiry } from "../types";
import { getFormConfig } from "../eventsApi";

type CannedMessage = { label: string; body: string };

type Props = {
  inquiry: Inquiry;
  onSend: (subject: string, body: string) => Promise<void>;
  onClose: () => void;
};

const TOOLBAR_ACTIONS = [
  { cmd: "bold", icon: "B", title: "Bold", style: "font-bold" },
  { cmd: "italic", icon: "I", title: "Italic", style: "italic" },
  { cmd: "underline", icon: "U", title: "Underline", style: "underline" },
] as const;

const SIGNATURE_HTML = `<p style="margin:0"><br></p><p style="margin:0">Thank You,</p><p style="margin:0">Tribal Axe Team</p>`;

export function EmailComposeModal({ inquiry, onSend, onClose }: Props) {
  const [subject, setSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [cannedMessages, setCannedMessages] = useState<CannedMessage[]>([]);
  const [showInsertMenu, setShowInsertMenu] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const insertMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    subjectRef.current?.focus();
  }, []);

  useEffect(() => {
    getFormConfig()
      .then((config) => {
        if (Array.isArray(config.canned_messages)) {
          setCannedMessages(config.canned_messages as CannedMessage[]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!showInsertMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (insertMenuRef.current && !insertMenuRef.current.contains(e.target as Node)) {
        setShowInsertMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showInsertMenu]);

  const execCommand = useCallback((cmd: string) => {
    document.execCommand(cmd, false);
    editorRef.current?.focus();
  }, []);

  const insertHtmlAtCursor = useCallback((html: string) => {
    editorRef.current?.focus();
    document.execCommand("insertHTML", false, html);
  }, []);

  const getEditorHtml = useCallback((): string => {
    if (!editorRef.current) return "";
    return editorRef.current.innerHTML;
  }, []);

  const getEditorText = useCallback((): string => {
    if (!editorRef.current) return "";
    return editorRef.current.innerText.trim();
  }, []);

  const handleInsertPlaceholder = useCallback(
    (tag: string, label: string) => {
      insertHtmlAtCursor(
        `<span contenteditable="false" style="background:#c0392b22;border:1px solid #c0392b55;border-radius:3px;padding:1px 5px;font-size:0.85em;color:#e8a0a0;white-space:nowrap;" data-placeholder="${tag}">${label}</span>&nbsp;`
      );
    },
    [insertHtmlAtCursor]
  );

  const handleInsertSignature = useCallback(() => {
    insertHtmlAtCursor(SIGNATURE_HTML);
  }, [insertHtmlAtCursor]);

  const handleInsertCanned = useCallback(
    (msg: CannedMessage) => {
      const html = msg.body
        .split(/\n{2,}/)
        .map((p) => `<p style="margin:0 0 0.75em">${p.replace(/\n/g, "<br>")}</p>`)
        .join("");
      insertHtmlAtCursor(html);
      setShowInsertMenu(false);
    },
    [insertHtmlAtCursor]
  );

  const handleSend = async () => {
    const text = getEditorText();
    if (!text) return;

    setSending(true);
    setError("");
    try {
      await onSend(subject, getEditorHtml());
    } catch (err) {
      setError((err as Error).message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  };

  const hasBookingUrl = !!inquiry.checkfront_booking_url;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div
        className="w-full max-w-2xl mx-4 rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl flex flex-col"
        style={{ maxHeight: "85vh" }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-white">Compose Email</h2>
            <p className="text-xs text-tribal-muted mt-0.5">
              To: {inquiry.first_name} {inquiry.last_name} &lt;{inquiry.email}&gt;
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-tribal-muted hover:text-white text-lg leading-none"
          >
            ✕
          </button>
        </div>

        {/* Subject */}
        <div className="border-b border-tribal-border px-5 py-3">
          <input
            ref={subjectRef}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full bg-transparent text-sm text-white placeholder-tribal-muted focus:outline-none"
          />
        </div>

        {/* Toolbar row 1: formatting */}
        <div className="flex items-center gap-1 border-b border-tribal-border px-5 py-2">
          {TOOLBAR_ACTIONS.map(({ cmd, icon, title, style }) => (
            <button
              key={cmd}
              type="button"
              title={title}
              onMouseDown={(e) => {
                e.preventDefault();
                execCommand(cmd);
              }}
              className={`rounded px-2.5 py-1 text-xs text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors ${style}`}
            >
              {icon}
            </button>
          ))}
          <span className="mx-1 h-4 w-px bg-tribal-border" />
          <button
            type="button"
            title="Bulleted list"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand("insertUnorderedList");
            }}
            className="rounded px-2.5 py-1 text-xs text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors"
          >
            • List
          </button>
          <button
            type="button"
            title="Numbered list"
            onMouseDown={(e) => {
              e.preventDefault();
              execCommand("insertOrderedList");
            }}
            className="rounded px-2.5 py-1 text-xs text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors"
          >
            1. List
          </button>
        </div>

        {/* Toolbar row 2: insert placeholders, signature, canned */}
        <div className="flex items-center gap-1 border-b border-tribal-border px-5 py-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-tribal-muted/60 mr-1">Insert:</span>

          <button
            type="button"
            title="Insert customer first name"
            onMouseDown={(e) => {
              e.preventDefault();
              handleInsertPlaceholder("first_name", "{first name}");
            }}
            className="rounded border border-tribal-border px-2 py-0.5 text-[11px] text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors"
          >
            {"{first name}"}
          </button>

          <button
            type="button"
            title={hasBookingUrl ? "Insert reservation link" : "No reservation — generate a quote first"}
            disabled={!hasBookingUrl}
            onMouseDown={(e) => {
              e.preventDefault();
              handleInsertPlaceholder("reservation_link", "{res link}");
            }}
            className="rounded border border-tribal-border px-2 py-0.5 text-[11px] text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {"{res link}"}
          </button>

          <button
            type="button"
            title={hasBookingUrl ? "Insert deposit payment link" : "No reservation — generate a quote first"}
            disabled={!hasBookingUrl}
            onMouseDown={(e) => {
              e.preventDefault();
              handleInsertPlaceholder("deposit_link", "{deposit link}");
            }}
            className="rounded border border-tribal-border px-2 py-0.5 text-[11px] text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {"{deposit link}"}
          </button>

          <span className="mx-1 h-4 w-px bg-tribal-border" />

          <button
            type="button"
            title="Insert signature block"
            onMouseDown={(e) => {
              e.preventDefault();
              handleInsertSignature();
            }}
            className="rounded border border-tribal-border px-2 py-0.5 text-[11px] text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors"
          >
            Signature
          </button>

          {cannedMessages.length > 0 && (
            <>
              <span className="mx-1 h-4 w-px bg-tribal-border" />
              <div className="relative" ref={insertMenuRef}>
                <button
                  type="button"
                  title="Insert a saved message template"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setShowInsertMenu((v) => !v);
                  }}
                  className="rounded border border-tribal-border px-2 py-0.5 text-[11px] text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors"
                >
                  Templates ▾
                </button>
                {showInsertMenu && (
                  <div className="absolute left-0 top-full mt-1 z-10 w-56 rounded-md border border-tribal-border bg-tribal-surface shadow-xl py-1">
                    {cannedMessages.map((msg, i) => (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleInsertCanned(msg);
                        }}
                        className="w-full text-left px-3 py-2 text-xs text-tribal-muted hover:bg-tribal-border hover:text-white transition-colors"
                      >
                        {msg.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Editor area */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            data-placeholder="Type your email message…"
            className="min-h-[200px] text-sm text-white leading-relaxed focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-tribal-muted empty:before:pointer-events-none"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="mx-5 mb-3 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-tribal-border px-5 py-4">
          <span className="text-xs text-tribal-muted">
            Ctrl+Enter to send
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-4 py-2 text-sm text-tribal-muted hover:text-white"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="rounded-md bg-tribal-red px-5 py-2 text-sm font-medium text-white hover:bg-tribal-red-dark disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send Email"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
